import React, { useEffect, useState } from 'react';
import { API_URL } from '../config/constants';
import Modal from './Modal';

interface Vendor {
  _id: string;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  address: string;
  note?: string;
  rating?: number;
}

interface ModalConfig {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  onConfirm?: () => void;
}

const VendorPool: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [modal, setModal] = useState<ModalConfig>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [newVendor, setNewVendor] = useState<Partial<Vendor>>({});

  const showModal = (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning', onConfirm?: () => void) => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
    setSelectedVendor(null);
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/vendors`);
      if (!response.ok) throw new Error('Satıcılar yüklenemedi');
      const data = await response.json();
      setVendors(data);
    } catch (error) {
      console.error('Satıcılar yüklenirken hata oluştu:', error);
      showModal('Hata', 'Satıcılar yüklenirken bir hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = async () => {
    try {
      if (!newVendor.companyName || !newVendor.name || !newVendor.email || !newVendor.phone || !newVendor.address) {
        showModal('Hata', 'Lütfen tüm zorunlu alanları doldurun', 'error');
        return;
      }

      const method = selectedVendor ? 'PUT' : 'POST';
      const url = selectedVendor 
        ? `${API_URL}/vendors/${selectedVendor._id}`
        : `${API_URL}/vendors`;

      const requestBody = selectedVendor
        ? { ...newVendor, _id: selectedVendor._id }
        : { ...newVendor };

      // Detaylı veri kontrolü
      console.log('Form verileri:', {
        newVendor,
        requestBody,
        method,
        url,
        selectedVendor: selectedVendor ? 'Güncelleme' : 'Yeni Kayıt'
      });

      // Zorunlu alan kontrolü
      const requiredFields = ['name', 'companyName', 'email', 'phone', 'address'] as const;
      const missingFields = requiredFields.filter(field => !requestBody[field as keyof typeof requestBody]);
      if (missingFields.length > 0) {
        console.log('Eksik alanlar:', missingFields);
        throw new Error(`Eksik alanlar: ${missingFields.join(', ')}`);
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody),
      });

      let data;
      const responseText = await response.text();
      console.log('Ham sunucu yanıtı:', responseText);
      
      try {
        data = JSON.parse(responseText);
        console.log('İşlenmiş sunucu yanıtı:', data);
      } catch (e) {
        console.error('JSON parse hatası:', e);
        data = { message: 'Sunucu yanıtı işlenemedi' };
      }

      console.log('Sunucu yanıtı detayları:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data
      });

      if (!response.ok) {
        throw new Error(
          data.message || 
          data.error || 
          `Sunucu hatası: ${response.status} ${response.statusText}`
        );
      }
      
      await fetchVendors();
      setIsAddModalOpen(false);
      setNewVendor({});
      setSelectedVendor(null);
      showModal('Başarılı', selectedVendor ? 'Üretici başarıyla güncellendi' : 'Üretici başarıyla eklendi', 'success');
    } catch (error) {
      console.error('İşlem detaylı hata:', error);
      const errorMessage = error instanceof Error ? error.message : 'Sunucu ile iletişim kurulurken bir hata oluştu';
      showModal(
        'Hata', 
        `Üretici ${selectedVendor ? 'güncellenirken' : 'eklenirken'} bir hata oluştu: ${errorMessage}`,
        'error'
      );
    }
  };

  const handleDeleteVendor = async (vendor: Vendor) => {
    try {
      const response = await fetch(`${API_URL}/vendors/${vendor._id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Satıcı silinemedi');
      
      await fetchVendors();
      showModal('Başarılı', 'Satıcı başarıyla silindi', 'success');
    } catch (error) {
      console.error('Silme hatası:', error);
      showModal('Hata', 'Satıcı silinirken bir hata oluştu', 'error');
    }
  };

  const confirmDelete = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    showModal(
      'Satıcıyı Sil',
      `"${vendor.name}" isimli satıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      'warning',
      () => handleDeleteVendor(vendor)
    );
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-light text-gray-800">Üretici Havuzu</h1>
          </div>
          <div className="flex gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Üretici Ara..."
                className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Yeni Üretici Ekle
            </button>
          </div>
        </div>

        <div className="bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Firma Adı
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Yetkili
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İletişim
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adres
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Not
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVendors.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      {searchTerm ? 'Arama kriterlerine uygun üretici bulunamadı.' : 'Henüz üretici eklenmemiş.'}
                    </td>
                  </tr>
                ) : (
                  filteredVendors.map((vendor) => (
                    <tr 
                      key={vendor._id} 
                      className="hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                      onClick={() => {
                        setSelectedVendor(vendor);
                        setIsDetailModalOpen(true);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500">
                        {vendor._id.slice(-6).toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {vendor.companyName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {vendor.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {vendor.phone}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-start gap-1">
                          <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {vendor.address}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {vendor.note || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVendor(vendor);
                              setNewVendor(vendor);
                              setIsAddModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(vendor);
                            }}
                            className="text-red-600 hover:text-red-900 transition-colors duration-200"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Yeni Üretici / Düzenleme Modalı */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setNewVendor({});
          setSelectedVendor(null);
        }}
        title={selectedVendor ? 'Üretici Düzenle' : 'Yeni Üretici Ekle'}
        type="info"
        message=""
        closeOnClickOutside={true}
        buttons={[
          {
            text: 'İptal',
            onClick: () => {
              setIsAddModalOpen(false);
              setNewVendor({});
              setSelectedVendor(null);
            },
            className: 'px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200'
          },
          {
            text: selectedVendor ? 'Güncelle' : 'Ekle',
            onClick: handleAddVendor,
            className: 'px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200'
          }
        ]}
      >
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Firma Adı
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newVendor.companyName || ''}
                onChange={(e) => setNewVendor({ ...newVendor, companyName: e.target.value })}
                placeholder="Firma adını girin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yetkili Adı
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newVendor.name || ''}
                onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                placeholder="Yetkili kişinin adını girin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-posta
              </label>
              <input
                type="email"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newVendor.email || ''}
                onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                placeholder="E-posta adresini girin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefon
              </label>
              <input
                type="tel"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newVendor.phone || ''}
                onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                placeholder="Telefon numarasını girin"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adres
            </label>
            <textarea
              className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              value={newVendor.address || ''}
              onChange={(e) => setNewVendor({ ...newVendor, address: e.target.value })}
              placeholder="Açık adresi girin"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Not
            </label>
            <textarea
              className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
              value={newVendor.note || ''}
              onChange={(e) => setNewVendor({ ...newVendor, note: e.target.value })}
              placeholder="Ek notlar (opsiyonel)"
            />
          </div>
        </div>
      </Modal>

      {/* Silme Onay Modalı */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        closeOnClickOutside={true}
        buttons={[
          {
            text: 'İptal',
            onClick: closeModal,
            className: 'px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200'
          },
          ...(modal.onConfirm ? [{
            text: 'Sil',
            onClick: () => {
              modal.onConfirm?.();
              closeModal();
            },
            className: 'px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200'
          }] : [])
        ]}
      />

      {/* Üretici Detay Modalı */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedVendor(null);
        }}
        title="Üretici Detayları"
        type="info"
        message=""
        closeOnClickOutside={true}
        buttons={[
          {
            text: 'Düzenle',
            onClick: () => {
              setIsDetailModalOpen(false);
              setNewVendor(selectedVendor || {});
              setIsAddModalOpen(true);
            },
            className: 'px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200'
          },
          {
            text: 'Kapat',
            onClick: () => {
              setIsDetailModalOpen(false);
              setSelectedVendor(null);
            },
            className: 'px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200'
          }
        ]}
      >
        {selectedVendor && (
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Üretici ID</div>
                <div className="mt-1 text-sm text-gray-900">{selectedVendor._id.slice(-6).toUpperCase()}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Firma Adı</div>
                <div className="mt-1 text-sm text-gray-900">{selectedVendor.companyName}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Yetkili Adı</div>
                <div className="mt-1 text-sm text-gray-900">{selectedVendor.name}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">E-posta</div>
                <div className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {selectedVendor.email}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Telefon</div>
                <div className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {selectedVendor.phone}
                </div>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">Adres</div>
              <div className="mt-1 text-sm text-gray-900 flex items-start gap-2">
                <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {selectedVendor.address}
              </div>
            </div>
            {selectedVendor.note && (
              <div>
                <div className="text-sm font-medium text-gray-500">Not</div>
                <div className="mt-1 text-sm text-gray-900">{selectedVendor.note}</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VendorPool; 